import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "plan-comparison-table" }
const _hoisted_2 = { class: "plan-comparison-title" }
const _hoisted_3 = { class: "feature-table-container" }
const _hoisted_4 = { scope: "col" }
const _hoisted_5 = {
  scope: "col",
  class: "highlighted"
}
const _hoisted_6 = { scope: "col" }
const _hoisted_7 = { scope: "col" }
const _hoisted_8 = {
  scope: "row",
  class: "section-title"
}
const _hoisted_9 = { class: "section-name" }
const _hoisted_10 = {
  scope: "row",
  class: "feature-title"
}
const _hoisted_11 = { class: "feature-name" }
const _hoisted_12 = {
  key: 0,
  class: "feature-description"
}

import { useI18n } from 'vue-i18n'

type Feature = string | boolean | number
interface PlanFeatures<T> {
  features: {
    title?: string
    numberOfUsers: T
    dashboardAccess: T
    offsetYourTeamsEmissions: T
    impactMarketplace: T
    marketingCalendar: T
    numberOfExtraTreesPerMonth: T
  }
  engagementHub: {
    title?: string
    publicProfile: T
    badgesAndWidgets: T
    qrCodeForProducts: T
    customerImpactWallet: T
    mediaPack: T
  }
  integrationsAndAutomations: {
    title?: string
    activeIntegrations: T
    basicAutomations: T
    advancedAutomations: T
  }
  network: {
    title?: string
    referralProgram: T
    partnerRewards: T
  }
  analytics: {
    title?: string
    basicAnalytics: T
    advancedAnalytics: T
  }
  api: {
    title?: string
    widgetApi: T
    greensparkApi: T
  }
  support: {
    title?: string
    chatAndEmail: T
    technicalSupport: T
    priorityCampaignSupport: T
  }
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PlanComparisonTable',
  setup(__props) {

const { t } = useI18n()

const featureCopies: PlanFeatures<{ title: string; description?: string }> = {
  features: {
    title: t('PlanComparisonTable.features'),
    numberOfUsers: {
      title: t('PlanComparisonTable.number_of_users'),
    },
    dashboardAccess: {
      title: t('PlanComparisonTable.dashboard_access'),
    },
    offsetYourTeamsEmissions: {
      title: t('PlanComparisonTable.offset_your_teams_emissions'),
      description: t('PlanComparisonTable.add_monthly_subscriptions'),
    },
    impactMarketplace: {
      title: t('PlanComparisonTable.impact_marketplace'),
      description: t('PlanComparisonTable.easily_access_projects'),
    },
    marketingCalendar: {
      title: t('PlanComparisonTable.marketing_calendar'),
    },
    numberOfExtraTreesPerMonth: {
      title: t('PlanComparisonTable.number_of_extra_trees_per_month'),
    },
  },
  engagementHub: {
    title: t('PlanComparisonTable.engagement_hub'),
    publicProfile: {
      title: t('PlanComparisonTable.public_profile'),
      description: t('PlanComparisonTable.create_sustainability_page'),
    },
    badgesAndWidgets: {
      title: t('PlanComparisonTable.badges_and_widgets'),
      description: t('PlanComparisonTable.showcase_your_impact'),
    },
    qrCodeForProducts: {
      title: t('PlanComparisonTable.qr_code_for_products'),
      description: t('PlanComparisonTable.bridge_the_gap'),
    },
    customerImpactWallet: {
      title: t('PlanComparisonTable.customer_impact_wallet'),
      description: t('PlanComparisonTable.connect_with_your_customers'),
    },
    mediaPack: {
      title: t('PlanComparisonTable.media_pack'),
    },
  },
  integrationsAndAutomations: {
    title: t('PlanComparisonTable.integrations_and_automations'),
    activeIntegrations: {
      title: t('PlanComparisonTable.active_integrations'),
      description: t('PlanComparisonTable.integrate_your_favourite_apps'),
    },
    basicAutomations: {
      title: t('PlanComparisonTable.basic_automations'),
      description: t('PlanComparisonTable.basic_automations_include'),
    },
    advancedAutomations: {
      title: t('PlanComparisonTable.advanced_automations'),
      description: t('PlanComparisonTable.includes_all_automations'),
    },
  },
  network: {
    title: t('PlanComparisonTable.network'),
    referralProgram: {
      title: t('PlanComparisonTable.referral_program'),
      description: t('PlanComparisonTable.give_and_receive_impact'),
    },
    partnerRewards: {
      title: t('PlanComparisonTable.partner_rewards'),
      description: t('PlanComparisonTable.invite_your_partners'),
    },
  },
  analytics: {
    title: t('PlanComparisonTable.analytics'),
    basicAnalytics: {
      title: t('PlanComparisonTable.basic_analytics'),
      description: t('PlanComparisonTable.gain_better_understanding'),
    },
    advancedAnalytics: {
      title: t('PlanComparisonTable.advanced_analytics_name'),
      description: t('PlanComparisonTable.deep_dive_into_analytics'),
    },
  },
  api: {
    title: t('PlanComparisonTable.api'),
    widgetApi: {
      title: t('PlanComparisonTable.widget_api'),
      description: t('PlanComparisonTable.display_our_widgets'),
    },
    greensparkApi: {
      title: t('PlanComparisonTable.greenspark_api'),
      description: t('PlanComparisonTable.integrate_impact_anywhere'),
    },
  },
  support: {
    title: t('PlanComparisonTable.support'),
    chatAndEmail: {
      title: t('PlanComparisonTable.chat_and_email'),
    },
    technicalSupport: {
      title: t('PlanComparisonTable.technical_support'),
    },
    priorityCampaignSupport: {
      title: t('PlanComparisonTable.priority_campaign_support'),
      description: t('PlanComparisonTable.receive_support'),
    },
  },
}

const INFINITY_SIMBOL = Number.POSITIVE_INFINITY

const plans: Record<string, PlanFeatures<Feature>> = {
  starter: {
    features: {
      numberOfUsers: '1',
      dashboardAccess: true,
      offsetYourTeamsEmissions: true,
      impactMarketplace: true,
      marketingCalendar: true,
      numberOfExtraTreesPerMonth: '1',
    },
    engagementHub: {
      publicProfile: t('PlanComparisonTable.basic'),
      badgesAndWidgets: true,
      qrCodeForProducts: false,
      customerImpactWallet: false,
      mediaPack: true,
    },
    integrationsAndAutomations: {
      activeIntegrations: '1',
      basicAutomations: true,
      advancedAutomations: false,
    },
    network: {
      referralProgram: true,
      partnerRewards: false,
    },
    analytics: {
      basicAnalytics: true,
      advancedAnalytics: false,
    },
    api: {
      widgetApi: true,
      greensparkApi: false,
    },
    support: {
      chatAndEmail: true,
      technicalSupport: false,
      priorityCampaignSupport: false,
    },
  },
  growth: {
    features: {
      numberOfUsers: '3',
      dashboardAccess: true,
      offsetYourTeamsEmissions: true,
      impactMarketplace: true,
      marketingCalendar: true,
      numberOfExtraTreesPerMonth: '15',
    },
    engagementHub: {
      publicProfile: t('PlanComparisonTable.full'),
      badgesAndWidgets: true,
      qrCodeForProducts: true,
      customerImpactWallet: t('PlanComparisonTable.basic'),
      mediaPack: true,
    },
    integrationsAndAutomations: {
      activeIntegrations: '3',
      basicAutomations: true,
      advancedAutomations: true,
    },
    network: {
      referralProgram: true,
      partnerRewards: true,
    },
    analytics: {
      basicAnalytics: true,
      advancedAnalytics: true,
    },
    api: {
      widgetApi: true,
      greensparkApi: false,
    },
    support: {
      chatAndEmail: true,
      technicalSupport: t('PlanComparisonTable.standard'),
      priorityCampaignSupport: false,
    },
  },
  premium: {
    features: {
      numberOfUsers: '8',
      dashboardAccess: true,
      offsetYourTeamsEmissions: true,
      impactMarketplace: true,
      marketingCalendar: true,
      numberOfExtraTreesPerMonth: '30',
    },
    engagementHub: {
      publicProfile: t('PlanComparisonTable.full'),
      badgesAndWidgets: true,
      qrCodeForProducts: true,
      customerImpactWallet: t('PlanComparisonTable.full'),
      mediaPack: true,
    },
    integrationsAndAutomations: {
      activeIntegrations: INFINITY_SIMBOL,
      basicAutomations: true,
      advancedAutomations: true,
    },
    network: {
      referralProgram: true,
      partnerRewards: true,
    },
    analytics: {
      basicAnalytics: true,
      advancedAnalytics: true,
    },
    api: {
      widgetApi: true,
      greensparkApi: t('PlanComparisonTable.calls', 5000),
    },
    support: {
      chatAndEmail: true,
      technicalSupport: t('PlanComparisonTable.priority'),
      priorityCampaignSupport: true,
    },
  },
  enterprise: {
    features: {
      numberOfUsers: INFINITY_SIMBOL,
      dashboardAccess: true,
      offsetYourTeamsEmissions: true,
      impactMarketplace: true,
      marketingCalendar: true,
      numberOfExtraTreesPerMonth: t('PlanComparisonTable.custom'),
    },
    engagementHub: {
      publicProfile: t('PlanComparisonTable.full'),
      badgesAndWidgets: true,
      qrCodeForProducts: true,
      customerImpactWallet: t('PlanComparisonTable.full'),
      mediaPack: true,
    },
    integrationsAndAutomations: {
      activeIntegrations: INFINITY_SIMBOL,
      basicAutomations: true,
      advancedAutomations: true,
    },
    network: {
      referralProgram: true,
      partnerRewards: true,
    },
    analytics: {
      basicAnalytics: true,
      advancedAnalytics: true,
    },
    api: {
      widgetApi: true,
      greensparkApi: t('PlanComparisonTable.custom'),
    },
    support: {
      chatAndEmail: true,
      technicalSupport: t('PlanComparisonTable.priority'),
      priorityCampaignSupport: true,
    },
  },
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("h3", _hoisted_2, _toDisplayString(_unref(t)('PlanComparisonTable.detailed_plan_comparison')), 1),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("table", null, [
        _createElementVNode("thead", null, [
          _createElementVNode("tr", null, [
            _createElementVNode("th", _hoisted_4, [
              _createElementVNode("h4", null, _toDisplayString(_unref(t)('PlanComparisonTable.plan_name_starter')), 1)
            ]),
            _createElementVNode("th", _hoisted_5, [
              _createElementVNode("h4", null, _toDisplayString(_unref(t)('PlanComparisonTable.plan_name_growth')), 1)
            ]),
            _createElementVNode("th", _hoisted_6, [
              _createElementVNode("h4", null, _toDisplayString(_unref(t)('PlanComparisonTable.plan_name_premium')), 1)
            ]),
            _createElementVNode("th", _hoisted_7, [
              _createElementVNode("h4", null, _toDisplayString(_unref(t)('PlanComparisonTable.plan_name_enterprise')), 1)
            ])
          ])
        ]),
        _createElementVNode("tbody", null, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.entries(featureCopies), ([sectionKey, section]) => {
            return (_openBlock(), _createElementBlock("tr", {
              class: "section",
              key: sectionKey
            }, [
              _createElementVNode("th", _hoisted_8, [
                _createElementVNode("p", _hoisted_9, _toDisplayString(section.title), 1)
              ]),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.entries(section as PlanFeatures<{ title: string; description?: string }>), ([featureKey, feature]) => {
                return (_openBlock(), _createElementBlock(_Fragment, { key: featureKey }, [
                  _createElementVNode("th", _hoisted_10, [
                    _createElementVNode("p", _hoisted_11, _toDisplayString(feature.title), 1),
                    (feature.description)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(feature.description), 1))
                      : _createCommentVNode("", true)
                  ]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.entries(plans), ([planKey, plan]) => {
                    return (_openBlock(), _createElementBlock(_Fragment, { key: planKey }, [
                      (plan[sectionKey][featureKey] === true)
                        ? (_openBlock(), _createElementBlock("td", {
                            key: 0,
                            class: _normalizeClass(["feature-value", { highlighted: planKey === 'growth' }])
                          }, [
                            _createVNode(_component_v_icon, {
                              color: "#212121",
                              size: "24px"
                            }, {
                              default: _withCtx(() => _cache[0] || (_cache[0] = [
                                _createTextVNode("mdi-check-circle-outline")
                              ])),
                              _: 1
                            })
                          ], 2))
                        : (plan[sectionKey][featureKey] === false)
                          ? (_openBlock(), _createElementBlock("td", {
                              key: 1,
                              class: _normalizeClass(["feature-value", { highlighted: planKey === 'growth' }])
                            }, [
                              _createVNode(_component_v_icon, {
                                color: "#9F9F9F",
                                size: "24px"
                              }, {
                                default: _withCtx(() => _cache[1] || (_cache[1] = [
                                  _createTextVNode("mdi-close-circle-outline")
                                ])),
                                _: 1
                              })
                            ], 2))
                          : (plan[sectionKey][featureKey] === Number.POSITIVE_INFINITY)
                            ? (_openBlock(), _createElementBlock("td", {
                                key: 2,
                                class: _normalizeClass(["feature-value", { highlighted: planKey === 'growth' }])
                              }, _cache[2] || (_cache[2] = [
                                _createElementVNode("svg", {
                                  xmlns: "http://www.w3.org/2000/svg",
                                  width: "24",
                                  height: "12",
                                  viewBox: "0 0 24 12",
                                  fill: "none"
                                }, [
                                  _createElementVNode("path", {
                                    d: "M9.74811 7.00888C10.2315 7.51285 10.7121 8.01368 11.2031 8.52566C10.9143 8.79424 10.6335 9.06653 10.3408 9.32453C10.0267 9.6014 9.71166 9.87884 9.37829 10.1308C8.82823 10.5467 8.23805 10.8991 7.58826 11.1316C7.24642 11.254 6.89779 11.3725 6.54238 11.4374C6.06182 11.5249 5.57052 11.5718 5.0812 11.5295C4.38931 11.4694 3.72088 11.3021 3.0999 10.9789C2.599 10.7183 2.14641 10.3857 1.76049 9.96865C1.17031 9.33053 0.768852 8.57972 0.53182 7.74367C0.41288 7.32407 0.328407 6.89304 0.322192 6.45113C0.318801 6.20086 0.283204 5.94916 0.29846 5.70032C0.331232 5.16288 0.432373 4.63573 0.61488 4.12775C0.835809 3.51366 1.13923 2.94276 1.55199 2.4405C2.11646 1.75347 2.80241 1.22633 3.622 0.87852C4.06753 0.689458 4.52549 0.547018 5.00323 0.498108C5.39338 0.458065 5.79088 0.440045 6.18132 0.468362C6.88705 0.519274 7.55662 0.732076 8.18635 1.05843C8.72652 1.33816 9.2246 1.68368 9.69641 2.07124C10.3592 2.61526 10.9734 3.21105 11.5667 3.83C12.0046 4.28678 12.4394 4.74643 12.8728 5.2075C13.1149 5.46492 13.344 5.73521 13.592 5.98663C14.0703 6.47172 14.5577 6.94738 15.0411 7.42704C15.5386 7.92101 16.0663 8.37722 16.6726 8.7316C17.1001 8.98158 17.5493 9.17208 18.0434 9.24187C18.7443 9.34083 19.4108 9.24701 20.0385 8.90236C20.5507 8.62119 20.9485 8.22448 21.2573 7.72622C21.4958 7.34124 21.6319 6.91878 21.7026 6.47859C21.7687 6.06557 21.7376 5.65112 21.6585 5.23581C21.5675 4.75672 21.3969 4.31939 21.1132 3.92868C20.6702 3.31916 20.0911 2.91759 19.3475 2.78144C19.1486 2.74511 18.9477 2.7045 18.7469 2.69878C18.0971 2.6799 17.4846 2.84122 16.9068 3.13697C16.4045 3.3941 15.9437 3.71474 15.5191 4.08828C15.2662 4.31081 15.014 4.53477 14.7668 4.76387C14.7012 4.8248 14.6619 4.81965 14.606 4.75758C14.4639 4.59998 14.3195 4.4441 14.1721 4.29136C13.8669 3.9753 13.559 3.66182 13.2477 3.34262C13.2708 3.31087 13.2841 3.28684 13.3027 3.26825C13.9342 2.64243 14.5916 2.04607 15.3343 1.55783C15.8782 1.2003 16.45 0.899972 17.0794 0.713484C17.4326 0.6088 17.7843 0.514698 18.1505 0.49439C18.5194 0.474082 18.8918 0.439759 19.2585 0.467504C20.2013 0.538724 21.0802 0.823032 21.8568 1.38335C22.6498 1.9554 23.2112 2.71279 23.5745 3.6255C23.7652 4.10459 23.9003 4.59826 23.9384 5.11368C23.97 5.54186 24.0212 5.97461 23.9907 6.39993C23.9189 7.3993 23.6025 8.3223 23.0369 9.15377C22.5744 9.83394 21.9893 10.3745 21.2867 10.7907C20.6931 11.1422 20.0532 11.3673 19.3783 11.4686C18.9839 11.5278 18.5765 11.5512 18.1793 11.5252C17.445 11.4774 16.7554 11.246 16.1016 10.9014C15.5414 10.6059 15.0301 10.2415 14.539 9.84137C13.7217 9.17522 13.0134 8.39438 12.2701 7.65071C11.8902 7.27059 11.5347 6.86529 11.1621 6.47744C10.5722 5.86364 9.98373 5.24811 9.38253 4.64574C8.86157 4.12375 8.30925 3.63494 7.6716 3.26082C7.24641 3.0114 6.79636 2.81719 6.30309 2.75312C6.08244 2.72452 5.85954 2.6962 5.63804 2.69821C5.02385 2.70335 4.46305 2.89842 3.96667 3.25853C3.32451 3.72417 2.89875 4.34599 2.68912 5.1234C2.56425 5.58591 2.55973 6.04783 2.61256 6.51749C2.6733 7.05807 2.86146 7.54832 3.16517 7.99509C3.64177 8.69556 4.29891 9.11201 5.12612 9.24072C5.85671 9.35427 6.5517 9.21612 7.22579 8.91351C7.87191 8.6232 8.4296 8.20474 8.96186 7.74682C9.22884 7.51714 9.47717 7.26487 9.74783 7.01002L9.74811 7.00888Z",
                                    fill: "#212121"
                                  })
                                ], -1)
                              ]), 2))
                            : (_openBlock(), _createElementBlock("td", {
                                key: 3,
                                class: _normalizeClass(["feature-value", { highlighted: planKey === 'growth' }])
                              }, _toDisplayString(plan[sectionKey][featureKey]), 3))
                    ], 64))
                  }), 128)),
                  _cache[3] || (_cache[3] = _createElementVNode("hr", null, null, -1))
                ], 64))
              }), 128))
            ]))
          }), 128))
        ])
      ])
    ])
  ]))
}
}

})